import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Paper from '@material-ui/core/Paper';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import get from 'lodash/get';
import filter from 'lodash/filter';
import axios from 'axios';
import { MdFilterList, MdEdit, MdFileDownload } from 'react-icons/lib/md';

import { API_BASE_URL } from '../../styles/constant';
import { getSelectedCandidatesList } from '../../services/sessionTrackServices';
import AppShell from '../../components/AppShell';
import Auth from '../../components/Auth';
import Loader from '../../components/Loader';
import DataTable from '../../components/DataTable';
import ModalFilter from '../../components/ModalFilter';
import ModalForm from '../../components/ModalForm';
import SessionSelector from '../../components/SessionSelector';
import SessionVisualization from '../../components/SessionVisualization';

class MPTrack extends React.Component {
  static propTypes = {
    authenticatedUserInfo: PropTypes.object.isRequired,
  };

  constructor(props) {
    super(props);

    this.state = {
      dataLoaded: false,
      candidates: [],
      headers: [],
      error: null,
      tabValue: 0,
      filterConfig: {},
      jypSession: 'MONSOON',
    };

    this.dataTableRef = null;
  }

  componentDidMount() {
    this.unsubscribe = getSelectedCandidatesList((candidates) => {
      const locationBasedCandidates = this.getLocationBasedCandidates(
        candidates,
        this.props.authenticatedUserInfo.type === 'ADMIN'
          ? 'OVERALL'
          : this.props.authenticatedUserInfo.location,
      );
      this.setState({ candidates: locationBasedCandidates });
    });

    this.fetchDatatableHeaders();
  }

  componentWillUnmount() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  }

  fetchDatatableHeaders = async () => {
    try {
      const {
        data: { data },
      } = await axios.options(
        `${API_BASE_URL}/mpTrack/datatable/?session=${this.state.jypSession}`,
      );
      this.setState({ dataLoaded: true, headers: data || [] });
    } catch (error) {
      this.setState({ dataLoaded: true, error: error.message });
    }
  };

  getLocationBasedCandidates = (candidates, city) => {
    const locationBasedCandidates = filter(candidates, (candidateInfo) => {
      if (city === 'OVERALL') {
        return true;
      }
      return candidateInfo.jypCity === city;
    });

    return locationBasedCandidates;
  };

  getFilteredCandidatesList = () => {
    let filteredCandidates = this.state.candidates;
    const filterKeys = Object.keys(this.state.filterConfig);

    if (filterKeys.length > 0) {
      filteredCandidates = filter(this.state.candidates, candidateData => filterKeys
        .map((filterKey) => {
          const filterData = this.state.filterConfig[filterKey];
          if (filterData.lookupType === 'equal') {
            if (Array.isArray(filterData.value)) {
              return filterData.value.includes(get(candidateData, filterKey));
            }
            return get(candidateData, filterKey) === filterData.value;
          }
          if (filterData.lookupType === 'geq') {
            return get(candidateData, filterKey) >= filterData.value;
          }
          if (filterData.lookupType === 'leq') {
            return get(candidateData, filterKey) <= filterData.value;
          }
          return false;
        })
        .every(val => val));
    }

    return filteredCandidates;
  };

  renderToolbars = (selectedRows) => {
    if (selectedRows.length === 0) {
      return (
        <ToolbarContainer>
          <SessionSelector
            onChange={(jypSession) => {
              this.setState({ jypSession }, this.fetchDatatableHeaders);
            }}
          />

          <ModalFilter
            apiEndpoint={`${API_BASE_URL}/mpTrack/filters`}
            trigger={(
              <IconButton aria-label="Filter list">
                <MdFilterList />
              </IconButton>
)}
            tooltipTitle="Filter"
            onApply={(filterConfig) => {
              this.setState({ filterConfig });
            }}
            onClear={() => {
              this.setState({ filterConfig: {} });
            }}
            initialValues={this.state.filterConfig}
          />

          {this.props.authenticatedUserInfo.type === 'ADMIN' && (
            <Tooltip title="Download CSV">
              <IconButton
                aria-label="Download CSV"
                onClick={() => {
                  const filterConfigQueryString = JSON.stringify(this.state.filterConfig);
                  const downloadUrl = `${API_BASE_URL}/mpTrack/downloadCsv/?session=${
                    this.state.jypSession
                  }&filters=${filterConfigQueryString}`;

                  window.open(downloadUrl, '_blank');
                }}
              >
                <MdFileDownload />
              </IconButton>
            </Tooltip>
          )}
        </ToolbarContainer>
      );
    }

    return (
      <ToolbarContainer>
        {selectedRows.length === 1 && (
          <ModalForm
            key="edit-mpTrack"
            tooltipTitle="Edit MP Track"
            trigger={(
              <IconButton aria-label="Edit MP Track">
                <MdEdit size={24} />
              </IconButton>
)}
            apiEndpoint={`${API_BASE_URL}/mpTrack/update/${selectedRows[0]}/?session=${
              this.state.jypSession
            }`}
            method="put"
            onClose={() => {
              if (this.dataTableRef) {
                this.dataTableRef.resetSelectedRows();
              }
            }}
          />
        )}
      </ToolbarContainer>
    );
  };

  render() {
    return (
      <div>
        <AppShell>
          {this.state.error ? (
            <Typography variant="subheading" color="error">
              {this.state.error}
            </Typography>
          ) : !this.state.dataLoaded ? (
            <Loader />
          ) : (
            <div>
              <TabsContainer>
                <Tabs
                  value={this.state.tabValue}
                  onChange={(event, tabValue) => {
                    this.setState({ tabValue });
                  }}
                >
                  <Tab label="Report" />
                  <Tab label="Visualization" />
                </Tabs>
              </TabsContainer>

              {this.state.tabValue === 0 && (
                <DataTable
                  ref={(comp) => {
                    this.dataTableRef = comp;
                  }}
                  title="MP Track"
                  tableHeaders={this.state.headers}
                  tableData={this.getFilteredCandidatesList()}
                  renderToolbars={selectedRows => this.renderToolbars(selectedRows)}
                />
              )}
              {this.state.tabValue === 1 && (
                <SessionVisualization apiEndPoint={`${API_BASE_URL}/mpTrack/dashboard`} />
              )}
            </div>
          )}
        </AppShell>
      </div>
    );
  }
}

export default props => <Auth>{({ authState }) => <MPTrack {...props} {...authState} />}</Auth>;

const TabsContainer = styled(Paper).attrs({
  elevation: 4,
})`
  margin-bottom: 16px;

  && {
    background: ${props => props.theme.palette.primary.main};
  }

  & * {
    color: ${props => props.theme.palette.primary.contrastText} !important;
  }
`;

const ToolbarContainer = styled.div`
  display: flex;
  align-items: center;

  & > * + * {
    margin-left: 10px !important;
  }
`;



// WEBPACK FOOTER //
// ./src/pages/MPTrack/MPTrack.js